// Site types
/**
 * Distinguishes content-owning domains ('standard', default) from unified
 * multi-site orchestrator domains ('orchestrator') that federate other
 * domains as sources instead of indexing their own content.
 */
export type DomainType = 'standard' | 'orchestrator';

/**
 * Structured profile describing what a standard domain covers.
 * Used by orchestrator domains to decide which source domain(s) to query.
 */
export interface AgentCard {
  description: string;
  capabilities: string[];
  example_questions: string[];
  topics: string[];
  exclusions: string[];
}

export interface Site {
  site_id: string;
  site_name: string;
  description?: string;
  domain_type?: DomainType;
  org_id: string;
  program_id?: string;
  env_id?: string;
  status?: 'active' | 'deleting';
  agent_card?: AgentCard | null;
  source_site_ids?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface SiteListResponse {
  sites: Site[];
}

// =============================================================================
// Model Provider Types
// =============================================================================

export type ModelProvider = 'azure_openai' | 'watsonx' | 'gemini';

// Azure OpenAI Configuration
export interface AzureOpenAILLMConfig {
  api_key: string;
  endpoint: string;
  api_version: string;
  model: string;
  deployment_id?: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  seed?: number;
}

export interface AzureOpenAIEmbeddingConfig {
  api_key: string;
  endpoint: string;
  api_version: string;
  model: string;
}

// WatsonX Configuration
export interface WatsonXLLMConfig {
  api_key: string;
  url?: string; // default: https://us-south.ml.cloud.ibm.com
  project_id: string;
  space_id?: string;
  model: string;
  deployment_id?: string;
  temperature?: number;
  max_tokens?: number;
  seed?: number;
  top_p?: number;
}

export interface WatsonXEmbeddingConfig {
  api_key: string;
  url?: string;
  project_id: string;
  space_id?: string;
  model: string;
}

// Gemini Configuration
export interface GeminiLLMConfig {
  api_key: string;
  model?: string; // default: gemini-1.5-flash
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  top_k?: number;
}

export interface GeminiEmbeddingConfig {
  api_key: string;
  model?: string; // default: models/text-embedding-004
}

// Union types for provider configs
export type LLMProviderConfig = AzureOpenAILLMConfig | WatsonXLLMConfig | GeminiLLMConfig;
export type EmbeddingProviderConfig = AzureOpenAIEmbeddingConfig | WatsonXEmbeddingConfig | GeminiEmbeddingConfig;

// Config wrappers with provider discriminator.
// Config uses Record<string, unknown> to support dynamic metadata-driven forms.
export interface LLMConfigWrapper {
  provider: string;
  config: Record<string, unknown>;
}

export interface EmbeddingConfigWrapper {
  provider: string;
  config: Record<string, unknown>;
}

// =============================================================================
// Provider Metadata Types (from GET /providers)
// =============================================================================

export interface FieldConstraints {
  ge?: number;
  le?: number;
}

export interface FieldSchema {
  name: string;
  label: string;
  type: 'string' | 'number' | 'integer' | 'boolean';
  required: boolean;
  secret?: boolean;
  default?: unknown;
  description?: string;
  constraints?: FieldConstraints;
  group: 'credentials' | 'model' | 'behavioral';
}

export interface ConfigTypeMetadata {
  models: string[];
  fields: FieldSchema[];
}

export interface ProviderMetadata {
  key: string;
  label: string;
  llm: ConfigTypeMetadata;
  embedding: ConfigTypeMetadata;
}

export interface ProvidersMetadataResponse {
  providers: ProviderMetadata[];
}

// Site configuration request (for create/update)
export interface SiteConfigRequest {
  site_name: string;
  description?: string;
  domain_type?: 'standard' | 'orchestrator';
  /** utility name (e.g. 'answering'/'judge'/'embedding') -> Command Center model_configuration id */
  utility_configurations?: Record<string, string>;
  use_default?: boolean;
  /** When true, resolve per-utility configuration live from the global system
   * defaults (bypassing the org allowlist); utility_configurations is ignored. */
  use_system_configuration?: boolean;
  agent_card?: AgentCard | null;
  source_site_ids?: string[];
}

// Model validation result
export interface ModelValidationResult {
  valid: boolean;
  error?: string;
  latency_ms?: number;
}

// Site config validation response
export interface SiteConfigValidationResponse {
  valid: boolean;
  validations?: Record<string, ModelValidationResult>;
}

/** A utility's currently selected Command Center model configuration -- metadata only, never credentials. */
export interface UtilityConfigurationInfo {
  configuration_id: string;
  configuration_name: string;
  source: 'system_ootb' | 'org_custom';
  is_deprecated: boolean;
  sunset_date?: string | null;
}

// Site config response (after create)
export interface SiteConfigResponse extends Site {
  utility_configurations?: Record<string, UtilityConfigurationInfo>;
  domain_type?: 'standard' | 'orchestrator';
  agent_card?: AgentCard | null;
  source_site_ids?: string[];
  use_system_configuration?: boolean;
}

// Feedback check types
export interface DimensionScore {
  score: number;
  reasoning: string;
}

export interface FeedbackCheckResponse {
  success: boolean;
  quality_score: number;
  verdict: 'good' | 'needs_improvement' | 'poor';
  relevance: DimensionScore | null;
  completeness: DimensionScore | null;
  faithfulness: DimensionScore | null;
  coherence: DimensionScore | null;
  overall_reasoning: string;
  llm_duration_ms: number;
  site_id: string;
  org_id: string;
  error?: string | null;
}

// Chat message types
/** Structured choice shown with a clarification request (API: clarification_options). */
export interface ClarificationOption {
  id: string;
  title: string;
  hint?: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isError?: boolean;
  /** Quality score from feedback check. null = loading, undefined = not requested or failed. */
  qualityScore?: FeedbackCheckResponse | null;
  /** Metadata for submitting user feedback (only on assistant messages). */
  conversationId?: string;
  turnNumber?: number;
  finalDocumentList?: DocumentRef[];
  /** False when the agent found no relevant context for the question. */
  answered?: boolean;
  /** True when the assistant is asking the user to narrow or clarify. */
  clarification?: boolean;
  /** Titled choices when clarification is true (from answer / history API). */
  clarificationOptions?: ClarificationOption[];
  /** Suggested follow-ups when answered is false. */
  suggestedAlternativeQuestions?: string[];
  /** Steps captured during streaming analysis (persisted for display after answer). */
  analysisSteps?: StreamingStep[];
  /** User-submitted feedback. undefined = not submitted. */
  userFeedback?: UserFeedbackSubmission;
  /** LLM reasoning behind the generated answer. */
  answerReasoning?: string;
}

// User feedback submission (from the feedback modal)
export interface UserFeedbackSubmission {
  relevance: DimensionScore;
  completeness: DimensionScore;
  faithfulness: DimensionScore;
  coherence: DimensionScore;
}

// Document reference from answer response
export interface DocumentRef {
  title: string;
  guid: string;
  relevance_score: number;
  published_url: string;
  /**
   * The site that actually owns this document. In an orchestrator domain the
   * cited document lives on a member site, not the conversation's own site, so
   * the indexed-content fallback link must be built from this rather than the
   * active conversation's siteId. Null for standard-domain answers.
   */
  source_site_id: string | null;
}

// Conversation history types
export interface ConversationSummary {
  conversation_id: string;
  first_question: string;
  updated_at: string;
  turn_count: number;
}

export interface ConversationTurn {
  turn_number: number;
  question: string;
  answer: string;
  created_at: string;
  final_document_list?: DocumentRef[];
  answered?: boolean;
  clarification?: boolean;
  clarification_options?: ClarificationOption[];
  suggested_alternative_questions?: string[];
  answer_reasoning?: string | null;
  // LLM-as-Judge scores
  quality_score?: number | null;
  relevance_score?: number | null;
  completeness_score?: number | null;
  faithfulness_score?: number | null;
  coherence_score?: number | null;
  relevance_reasoning?: string | null;
  completeness_reasoning?: string | null;
  faithfulness_reasoning?: string | null;
  coherence_reasoning?: string | null;
  overall_reasoning?: string | null;
  verdict?: string | null;
  // User feedback scores (null if user has not submitted feedback)
  user_relevance_score?: number | null;
  user_relevance_reasoning?: string | null;
  user_completeness_score?: number | null;
  user_completeness_reasoning?: string | null;
  user_faithfulness_score?: number | null;
  user_faithfulness_reasoning?: string | null;
  user_coherence_score?: number | null;
  user_coherence_reasoning?: string | null;
}

export interface ConversationListResponse {
  conversations: ConversationSummary[];
}

export interface ConversationTurnsResponse {
  conversation_id: string;
  turns: ConversationTurn[];
}

// SSE streaming event types
export type StreamEventType =
  | 'phase'
  | 'query_analysis'
  | 'first_pass'
  | 'decision'
  | 'iteration'
  | 'node_enrichment'
  | 'answer_chunk'
  | 'complete'
  | 'error'
  | 'planning_analysis'
  | 'planning_group_start'
  | 'planning_sub_query_complete'
  | 'source_routing'
  | 'source_start'
  | 'source_complete'
  | 'unified_context_ready';

export interface StreamEvent {
  event: StreamEventType;
  // SSE event data is dynamic and varies per event type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
}

export interface StreamingStep {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: Record<string, any>;
}

/** Pre-ranked starter questions from GET /sites/{siteId}/suggested-questions */
export interface SuggestedQuestionItem {
  query_id: string;
  question: string;
  hit_count: number;
}

export interface SuggestedQuestionsResponse {
  suggestions: SuggestedQuestionItem[];
  source: 'cache_hits' | 'recent' | 'mixed' | 'agent_card';
}

// Answer API response
export interface AnswerResponse {
  success: boolean;
  answer: string;
  answer_reasoning: string;
  question: string;
  total_iterations: number;
  context_nodes_count: number;
  final_document_list: DocumentRef[];
  site_id: string;
  org_id: string;
  conversation_id?: string | null;
  turn_number?: number | null;
  answered?: boolean;
  clarification?: boolean;
  clarification_options?: ClarificationOption[];
  suggested_alternative_questions?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sub_query_results?: Record<string, any>;
  error?: string | null;
}

// Delete status response (from DELETE /sites/{siteId} and GET /sites/{siteId}/delete-status)
export interface DeleteStatusResponse {
  site_id: string;
  status: string;
}

// Error response
export interface ErrorResponse {
  error: string;
  detail?: string;
}

// =============================================================================
// Indexing Job Types
// =============================================================================

// Response from POST /sites/{siteId}/bulk-index/presign
export interface PresignResponse {
  job_id: string;
  upload_token: string;
  presigned_url: string;
  expires_in: number;
}

// One part's presigned PUT URL, returned by the multipart initiate endpoint
export interface MultipartUploadPart {
  part_number: number;
  presigned_url: string;
}

// Response from POST /sites/{siteId}/bulk-index/multipart/initiate
export interface MultipartInitiateResponse {
  job_id: string;
  upload_id: string;
  upload_token: string;
  part_size_bytes: number;
  parts: MultipartUploadPart[];
  expires_in: number;
}

// A part's S3 ETag, submitted back to the multipart complete endpoint
export interface CompletedPart {
  part_number: number;
  etag: string;
}

// Response from POST /sites/{siteId}/bulk-index (and /confirm, /multipart/{jobId}/complete)
export interface ZipIndexResponse {
  job_id: string;
  // "queued" (201, started now) | "waiting" (202, admitted to the admission
  // queue behind a running job). Plain string server-side -- treat unknown
  // values defensively.
  status: string;
  message: string;
  site_id: string;
  // Always null at creation (the archive is not scanned until the job runs).
  total_files: number | null;
}

// Response from POST /sites/{siteId}/index (single-file save)
// Returned immediately -- poll GET /sites/{siteId}/index?job_id=... for the outcome.
export interface SingleFileIndexResponse {
  job_id: string;
  status: string;
}

// Per-file indexing status within a bulk job
export interface FileStatus {
  status: 'PENDING' | 'INDEXING' | 'SUCCESS' | 'ERROR' | 'DEFERRED' | 'RETRYING' | 'CANCELLED' | 'SKIPPED' | 'PASS1_COMPLETE';
  reason: string | null;
}

// Response from GET /sites/{siteId}/index
export interface IndexingStatusResponse {
  job_id: string;
  site_id: string;
  // 'waiting' is synthesized for a job still in the admission queue (no
  // :IndexingStatus row yet); it transitions to 'queued'/'running' on dispatch.
  status: 'waiting' | 'queued' | 'running' | 'interrupted' | 'completed' | 'failed' | 'cancelled';
  total_files: number;
  processed_files: number;
  failed_files: number;
  // Per-file CANCELLED count (PENDING files flipped to CANCELLED when the job
  // was cancelled). Additive; 0 for jobs that were never cancelled.
  cancelled_files: number;
  // Per-file SKIPPED count (files whose content was unchanged since the last
  // successful index, so re-processing was skipped). Additive; 0 for jobs with
  // no skips. Kept distinct from processed_files (not folded into it).
  skipped_files: number;
  // Two-phase DITA indexing: files whose Pass 1 (structural graph) is done but
  // whose Pass 2 (context/vectors/FTS/quality/entities) is still owed. NOT part
  // of processed_files (strictly INDEXED), but surfaced so progress is visible
  // during Pass 1 -- no file is INDEXED until the barrier dispatches Pass 2.
  // Additive; 0 for single-pass (non-DITA) jobs or once Pass 2 has drained.
  pass1_complete_files?: number;
  // Non-terminal per-file buckets, additive (0 when absent).
  indexing_files?: number;
  pending_files?: number;
  retrying_files?: number;
  // flameback-data's two-phase barrier marker, passed through verbatim.
  pass_state?: 'PASS1_REGISTERING' | 'BARRIER_CROSSED' | 'PASS2_COMPLETE' | null;
  // UI-facing phase derived from pass_state: 'single_pass' (no two-phase concept
  // -- use terminal-only progress math), 'structuring' (Pass 1), 'enriching'
  // (Pass 2), or 'done'. null on a status-filtered page / unknown pass_state.
  phase?: 'single_pass' | 'structuring' | 'enriching' | 'done' | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  cancellation_requested: boolean;
  last_heartbeat: string | null;
  health: 'healthy' | 'warning' | 'stale' | null;
  base_path: string;
  root_map_path: string | null;
  resume_count: number;
  max_resume_attempts: number;
  files: Record<string, FileStatus>;
  triggered_by: string | null;
  source_type: string | null;
}

// Response from PUT /sites/{siteId}/index (cancel)
export interface IndexCancelResponse {
  job_id: string;
  status: string;
  message: string;
}

// Lightweight summary of one indexing job (for history view)
export interface IndexingHistoryItem {
  job_id: string;
  // 'waiting' appears only for a synthetic row surfaced from live polling of a
  // still-queued job; the history API itself does not emit it until dispatch.
  status: 'waiting' | 'queued' | 'running' | 'interrupted' | 'completed' | 'failed' | 'cancelled';
  total_files: number;
  processed_files: number;
  failed_files: number;
  // Terminal per-file CANCELLED / SKIPPED counts, mirroring IndexingStatusResponse.
  // Additive; 0 for jobs with no cancellations/skips. processed_files stays the
  // strict INDEXED count and never folds these in.
  cancelled_files: number;
  skipped_files: number;
  // Two-phase DITA indexing: PASS1_COMPLETE count + derived phase for active
  // (gap) rows, mirroring IndexingStatusResponse. Additive; 0/undefined for
  // terminal, single-pass, or no-gap rows the history endpoint did not poll.
  pass1_complete_files?: number;
  phase?: 'single_pass' | 'structuring' | 'enriching' | 'done' | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  base_path: string | null;
  root_map_path: string | null;
  triggered_by: string | null;
  source_type: string | null;
}

// Response from GET /sites/{siteId}/bulk-index/history
export interface IndexingHistoryResponse {
  site_id: string;
  jobs: IndexingHistoryItem[];
  total: number;
}

// =============================================================================
// Site Stats Types
// =============================================================================

// A single document entry in the paginated list
export interface DocumentListItem {
  asset_guid: string;
  title: string;
  type: string;
  indexed_at: string | null;
  published_url: string;
  published_context_root: string | null;
  quality_score: number | null;
}

// ---- Folder hierarchy (content_nodes tree) ----

export interface Crumb {
  id: string;
  name: string;
}

export interface ContentNodeChild {
  id: string;
  name: string;
  is_folder: boolean;
  modified: string | null;
  parent_folder_id: string | null;
  /** DITA topic type for DITA files (null for folders/non-DITA). */
  type?: string | null;
  /** DITA title from dita_fts_documents (null for folders/non-DITA). */
  title?: string | null;
  /** Stored published context root for DITA maps (null for folders/non-maps). */
  published_context_root: string | null;
}

// Response of GET /nodes/resolve?id= -- resolves a bare id of unknown kind
// (folder or file) so the caller doesn't have to guess. `breadcrumb` is the
// folder chain being "looked at": the node's own chain if it's a folder, or
// its parent's chain if it's a file (the file's own crumb is appended by
// the caller, same as opening a file from a folder listing).
export interface NodeResolveResponse {
  id: string;
  name: string;
  is_folder: boolean;
  parent_folder_id: string | null;
  breadcrumb: Crumb[];
}

// Response of GET /documents?parent_folder_id=
export interface FolderBrowseResponse {
  breadcrumb: Crumb[];
  children: ContentNodeChild[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface FolderCreateResponse {
  id: string;
  name: string;
  parent_folder_id: string;
  is_folder: boolean;
  modified: string | null;
}

export interface DeleteJobResponse {
  job_id: string;
}

export interface DeleteJobStatus {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  processed: number;
  total: number;
  stage: string | null;
  error?: string | null;
}

// DITA quality score from automated analysis
export interface CriterionDetail {
  score: number;
  summary: string;
}

export interface DitaQualityScore {
  score: number | null;
  details: Record<string, CriterionDetail> | null;
  suggestions?: Record<string, string> | null;
  scored_at: string | null;
}

// Full metadata for a single document (from GET /document/metadata)
export interface DocumentMetadataResponse {
  asset_guid: string;
  title: string;
  type: string;
  indexed_at: string | null;
  published_url: string;
  dita_quality: DitaQualityScore | null;
  judge: TopicQualityResponse | null;
  user: TopicQualityResponse | null;
  reference_count: number;
}

// Response for retrieving a single document's content
export interface DocumentContentResponse {
  asset_guid: string;
  title: string;
  type: string;
  content: string;
}

// Response for batch document deletion
export interface DocumentDeleteResponse {
  deleted_count: number;
  failed: string[];
  message: string;
}

// Published URL mapping for bulk update
export interface PublishedUrlMapping {
  asset_guid: string;
  published_url: string;
}

// Response for bulk published URL update
export interface PublishedUrlUpdateResponse {
  updated_count: number;
  not_found_count: number;
  updated_guids: string[];
  not_found_guids: string[];
}

// =============================================================================
// Search Types
// =============================================================================

export interface SearchResultItem {
  node_id: number | null;
  asset_id: string;
  title: string;
  type: string;
  content_text: string;
  indexed_at: string | null;
  score: number;
  fts_score: number | null;
  semantic_score: number | null;
}

export interface SearchResponse {
  search_type: string;
  results: SearchResultItem[];
  total: number;
}

// =============================================================================
// RBAC Types (role-based)
// =============================================================================

export enum Permission {
  // Site-scoped
  MANAGE_CONTENT = 'site:manage:content',
  DELETE_CONTENT = 'site:delete:content',
  UPDATE_SITE = 'site:update:site',
  DELETE_SITE = 'site:delete:site',
  MANAGE_EVALS = 'site:manage:evals',
  MANAGE_ACCESS_CONTROL = 'site:manage:access_control',
  DOWNLOAD_SIGNING_KEY = 'site:access_control:key_download',
  // Org-scoped
  MANAGE_USER = 'org:manage:user',
  CREATE_SITE = 'org:create:site',
  MANAGE_ORG = 'org:manage:org',
  GLOBAL_ADMIN = "global:admin:platform"
}

export type RoleScopeType = 'org' | 'site' | 'global';

export const PERMISSION_SCOPE: Record<string, RoleScopeType> = {
  [Permission.MANAGE_ORG]: 'org',
  [Permission.MANAGE_USER]: 'org',
  [Permission.CREATE_SITE]: 'org',
  [Permission.MANAGE_CONTENT]: 'site',
  [Permission.DELETE_CONTENT]: 'site',
  [Permission.UPDATE_SITE]: 'site',
  [Permission.DELETE_SITE]: 'site',
  [Permission.MANAGE_EVALS]: 'site',
  [Permission.MANAGE_ACCESS_CONTROL]: 'site',
  [Permission.DOWNLOAD_SIGNING_KEY]: 'site',
  [Permission.GLOBAL_ADMIN]: 'global',
};

export interface RoleDefinition {
  /** Stable role identifier (key). Built-ins: super_admin, site_admin, read_org. */
  role_id: string;
  /** Display label (built-in labels resolved server-side). */
  role_name: string;
  permissions: string[];
}

/** One assignable/visible permission with its backend-provided display name.
 *  Served by GET /flameback/api/v1/permissions. */
export interface PermissionCatalogEntry {
  /** Stable Permission enum value, e.g. `org:create:site`. */
  permission_id: string;
  /** Human-facing display name, e.g. `org:create:domain`. */
  permission_name: string;
  scope: RoleScopeType;
  /** False for permissions that cannot be granted via custom roles (e.g. global:admin:platform). */
  assignable: boolean;
}

export interface PermissionCatalogResponse {
  permissions: PermissionCatalogEntry[];
}

/** Returned by GET /orgs/{orgId}/users/me/acl */
export interface UserAclResponse {
  user_id: string;
  site_id?: string | null;
  org_roles: RoleDefinition[];
  site_roles: RoleDefinition[];
  org_permissions: string[];
  effective_permissions: string[];
  global_roles: RoleDefinition[];
  global_permissions: string[];
  publishing_enabled: boolean;
  /** Effective org upload limits (backend-resolved: per-org override or default). */
  zip_size_limit_mb: number;
  pdf_size_limit_mb: number;
  max_dita_files_per_zip: number;
}

/** Helper: check if a permission is in the effective set */
export function hasPermission(permissions: string[] | undefined | null, perm: string): boolean {
  return permissions?.includes(perm) ?? false;
}

// ----- Org-Level Role Definitions -----

export interface OrgRoleDefinition {
  /** Stable role identifier (key) used by assignments, API paths and filters. */
  role_id: string;
  /** Editable display label (built-in labels resolved server-side). */
  role_name: string;
  scope: RoleScopeType;
  description?: string | null;
  permissions: string[];
  is_system_role: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface OrgRolesListResponse {
  roles: OrgRoleDefinition[];
}

export interface CreateRoleRequest {
  role_name: string;
  scope: RoleScopeType;
  description?: string;
  permissions: string[];
}

export interface UpdateRoleRequest {
  role_name?: string;
  description?: string;
  permissions?: string[];
}

// ----- Org-Level User Assignments -----

export interface RoleAssignment {
  /** Stable role identifier (key). */
  role_id: string;
  /** Display label (built-in labels resolved server-side). */
  role_name: string;
  site_id?: string | null;
  is_org_role: boolean;
  created_at?: string | null;
  created_by?: string | null;
}

export interface OrgUserEntry {
  user_id: string;
  display_name?: string | null;
  last_seen_at?: string | null;
  assignments: RoleAssignment[];
  is_org_admin: boolean;
  has_read_access: boolean;
}

export interface OrgUsersListResponse {
  users: OrgUserEntry[];
  total: number;
  total_all: number;
  total_authorized: number;
  total_pending: number;
  page: number;
  page_size: number;
}

export interface OrgUsersQueryParams {
  page?: number;
  pageSize?: number;
  role?: string;
  access?: 'authorized' | 'pending';
  search?: string;
  excludeUserId?: string;
}

export interface RoleSiteAssignment {
  /** Stable role identifier to assign (NOT the label). */
  role_id: string;
  site_id?: string | null;
}

export interface AddMultiSiteRoleRequest {
  assignments: RoleSiteAssignment[];
}

export interface ReplaceUserRolesRequest {
  assignments: RoleSiteAssignment[];
}

// ----- Org Security: New-User Access Policy -----

/**
 * What a brand-new user automatically receives the first time they sign in to
 * an org (customer-controlled). Served by
 * GET/PUT /orgs/{orgId}/security/new-user-policy.
 *   - `none`           — no access until an admin approves (lands in Pending).
 *   - `read_org`       — can sign in and browse.
 *   - `domain_creator` — can sign in and create their own domains.
 */
export type NewUserDefaultRole = 'none' | 'read_org' | 'domain_creator';

/** Response body of GET/PUT /orgs/{orgId}/security/new-user-policy. */
export interface NewUserPolicyResponse {
  new_user_default_role: NewUserDefaultRole;
}

/** Request body of PUT /orgs/{orgId}/security/new-user-policy. */
export interface NewUserPolicyUpdateRequest {
  new_user_default_role: NewUserDefaultRole;
}

// =============================================================================
// Feedback Analytics Types
// =============================================================================

// Per-site feedback summary (from GET /sites/{siteId}/analytics/feedback/summary)
export interface FeedbackSummaryResponse {
  site_id: string;
  avg_quality: number;
  avg_relevance: number;
  avg_completeness: number;
  avg_faithfulness: number;
  avg_coherence: number;
  total_evaluations: number;
}

// All-sites feedback summary (from GET /analytics/feedback/summary)
export interface AllSitesFeedbackSummaryResponse {
  sites: FeedbackSummaryResponse[];
  total_sites: number;
}

// Topic quality data (individual topic)
export interface TopicQualityResponse {
  asset_guid: string;
  title: string;
  avg_quality: number | null;
  avg_relevance: number | null;
  avg_completeness: number | null;
  avg_faithfulness: number | null;
  avg_coherence: number | null;
  feedback_count: number;
}

// List of topics with quality scores
export interface TopicQualityListResponse {
  topics: TopicQualityResponse[];
  count: number;
}

// User feedback summary (from GET /sites/{siteId}/analytics/user-feedback/summary)
export interface UserFeedbackSummaryResponse {
  site_id: string;
  avg_quality: number;
  avg_relevance: number;
  avg_completeness: number;
  avg_faithfulness: number;
  avg_coherence: number;
  total_evaluations: number;
  unique_users: number;
}

// Time-series trend bucket
export interface TrendBucket {
  bucket: string;
  avg_quality: number;
  avg_relevance: number;
  avg_completeness: number;
  avg_faithfulness: number;
  avg_coherence: number;
  count: number;
}

// User feedback trends (from GET /sites/{siteId}/analytics/user-feedback/trends)
export interface UserFeedbackTrendsResponse {
  site_id: string;
  start_date: string;
  end_date: string;
  interval: string;
  buckets: TrendBucket[];
  total_feedback: number;
}

// Per-dimension comparison between user and judge
export interface DimensionComparison {
  dimension: string;
  user_avg: number;
  judge_avg: number;
  mae: number;
}

// Human vs LLM judge comparison (from GET /sites/{siteId}/analytics/feedback/comparison)
export interface FeedbackComparisonResponse {
  site_id: string;
  matched_count: number;
  dimensions: DimensionComparison[];
  user_avg_quality: number;
  judge_avg_quality: number;
  verdict_agreement_rate: number;
}

// Single rated conversation turn (from GET /sites/{siteId}/analytics/feedback/rows)
export interface FeedbackRow {
  created_by: string | null;
  conversation_id: string;
  question: string | null;
  normalised_question: string | null;
  answer: string | null;
  liked: boolean | null;
  disliked: boolean | null;
  feedback_comment: string | null;
  updated_at: string | null;
}

export interface FeedbackRowsResponse {
  rows: FeedbackRow[];
}

// =============================================================================
// Usage Analytics Types
// =============================================================================

// Usage trend bucket (from GET /sites/{siteId}/analytics/usage/trends)
export interface UsageTrendBucket {
  bucket: string;
  count: number;
}

export interface UsageTrendsResponse {
  site_id: string;
  start_date: string;
  end_date: string;
  interval: string;
  channel?: string | null;
  buckets: UsageTrendBucket[];
  total_queries: number;
}

// Channel usage entry (per-channel count for a user)
export interface ChannelUsageEntry {
  channel: string;
  count: number;
}

// Top user entry (from GET /sites/{siteId}/analytics/usage/top-users)
export interface UserUsageEntry {
  user_id: string;
  query_count: number;
  channel_breakdown?: ChannelUsageEntry[] | null;
}

export interface TopUsersResponse {
  site_id: string;
  channel?: string | null;
  users: UserUsageEntry[];
}

// Referenced topic entry (from GET /sites/{siteId}/analytics/usage/topics)
export interface ReferencedTopicEntry {
  asset_guid: string;
  title: string;
  reference_count: number;
}

export interface ReferencedTopicsResponse {
  site_id: string;
  topics: ReferencedTopicEntry[];
  count: number;
}

// =============================================================================
// LLM Latency Analytics Types
// =============================================================================

export interface LLMLatencyBucket {
  bucket: string;
  avg_latency_ms: number;
  query_count: number;
}

export interface LLMModelLatencySeries {
  llm_model: string;
  buckets: LLMLatencyBucket[];
}

export interface LLMLatencyTrendsResponse {
  site_id: string;
  start_date: string;
  end_date: string;
  interval: string;
  models: LLMModelLatencySeries[];
  total_queries: number;
}

// =============================================================================
// Answer Success Rate Analytics Types
// =============================================================================

export interface AnswerSuccessBucket {
  bucket: string;
  answered_count: number;
  unanswered_count: number;
  total: number;
}

export interface AnswerSuccessRateResponse {
  site_id: string;
  start_date: string;
  end_date: string;
  interval: string;
  buckets: AnswerSuccessBucket[];
  total_answered: number;
  total_unanswered: number;
  total_queries: number;
}

// =============================================================================
// Unanswered Questions Analytics Types
// =============================================================================

export interface UnansweredDocumentRef {
  title: string;
  guid: string;
  published_url: string;
  source_site_id: string | null;
  source_name: string | null;
}

export interface UnansweredQuestionRow {
  question: string;
  normalised_query: string | null;
  documents_searched: UnansweredDocumentRef[];
  created_at: string;
}

export interface UnansweredQuestionsResponse {
  site_id: string;
  rows: UnansweredQuestionRow[];
  page: number;
  page_size: number;
  total_count: number;
}

export interface CacheHitRateBucket {
  bucket: string;
  cache_hit_count: number;
  cache_miss_count: number;
  total: number;
}

export interface CacheHitRateResponse {
  site_id: string;
  start_date: string;
  end_date: string;
  interval: string;
  channel: string | null;
  buckets: CacheHitRateBucket[];
  total_cache_hits: number;
  total_cache_misses: number;
  total_queries: number;
}

// GET /sites/{siteId}/analytics/usage/rating-trends
export interface RatingTrendBucket {
  bucket: string;
  liked_count: number;
  disliked_count: number;
  total: number;
}

export interface RatingTrendsResponse {
  site_id: string;
  start_date: string;
  end_date: string;
  interval: string;
  buckets: RatingTrendBucket[];
  total_liked: number;
  total_disliked: number;
  total_rated: number;
}

// OAuth and Authentication Types

// IMS Product Context (for extracting org info)
export interface ImsProdCtx {
  orig_sys?: string;
  owningEntity?: string; // This is the org_id
  serviceCode?: string;
  ident?: string;
  groupid?: string;
  label?: string;
  user_visible_name?: string;
}

export interface ImsProjectedProductContext {
  prodCtx?: ImsProdCtx;
}

// IMS User Profile
export interface ImsUserProfile {
  anonymous?: boolean;
  account_type?: string;
  displayName?: string;
  last_name?: string;
  first_name?: string;
  userId?: string;
  email?: string;
  projectedProductContext?: ImsProjectedProductContext[];
}

// MCP tool configuration (from dedicated /mcp-tool-config endpoint)

/** Where the published MCP tool description came from. */
export type McpDescriptionSource = 'agent_card' | 'manual' | 'default';

/**
 * Whether the Agent Card carries enough signal to publish over MCP.
 * Gates MCP installation: an external client can only route to this tool
 * if the card describes what the site covers and what it does not.
 */
export interface AgentCardReadiness {
  is_ready: boolean;
  missing_fields: string[];
  required_fields: string[];
  minimums: Record<string, number>;
}

export interface McpToolConfigResponse {
  tool_name: string;
  tool_description: string;
  description_source: McpDescriptionSource;
  agent_card: AgentCardReadiness;
  /** The Agent Card was edited without re-rendering the description. */
  is_out_of_sync: boolean;
  api_key_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface UpdateMcpToolConfigRequest {
  /** Omit to keep the stored name; null resets it to the default derived from the site name. */
  tool_name?: string | null;
  /** Ignored once the Agent Card is complete -- the card wins. */
  tool_description?: string | null;
}

// Widget configuration stored on the server
export interface SiteWidgetConfig {
  heading?: string;
  logo_url?: string;
  /** Customer accent colour. `null` = the widget's built-in default. */
  color?: string | null;
  /** Display mode. `null` = the widget's built-in default. */
  mode?: 'popup' | 'sidebar' | null;
  /** SSE streaming. `null` = the widget's built-in default. */
  streaming?: boolean | null;
  custom_css?: string;
  widget_allowed_urls?: string[] | null;
  disclaimer_text?: string | null;
  disclaimer_link_label?: string | null;
  disclaimer_link_url?: string | null;
  metadata_error_message?: string | null;
  api_key_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

// =============================================================================
// Domain Filter Settings Types (DITAVAL include/exclude, applied to all channels)
// =============================================================================

/** Domain-level DITAVAL filter: attribute -> allowed/blocked values. */
export interface DomainFilterConfig {
  include?: Record<string, string[]>;
  exclude?: Record<string, string[]>;
}

/** Response from GET/PUT /platform/api/v1/sites/{siteId}/filter-settings */
export interface DomainFilterSettings {
  filter_settings: DomainFilterConfig | null;
  updated_at: string | null;
}

// =============================================================================
// Access Control Types (trusted metadata injection — __ACCESS_CONTROL__ node)
// =============================================================================

/** Allowed metadata vocabulary: attribute key -> permitted values. */
export type AllowedMetadata = Record<string, string[]>;

/** Schema portion of access control — safe to return in public API responses. */
export interface AccessControlConfigPayload {
  allowed_metadata: AllowedMetadata;
}

/** Response from GET/PUT /platform/api/v1/sites/{siteId}/access-control */
export interface AccessControlResponse {
  access_control_config: AccessControlConfigPayload | null;
  has_signing_key: boolean;
  key_created_at: string | null;
  updated_at: string | null;
}

/** Response from the signing-key endpoints — `signing_secret` is returned once on rotation. */
export interface SigningKeyResponse {
  key_id: string;
  signing_secret: string;
  created_at: string;
  previous_key_expires_at?: string | null;
}

// =============================================================================
// User Management Types (Flameback __USER__ node)
// =============================================================================

export interface EnsureUserResponse {
  user_id: string;
  org_id: string;
  display_name?: string | null;
  is_new_user: boolean;
  terms_accepted: boolean;
  terms_accepted_at?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  has_read_access: boolean;
  org_admin_email?: string | null;
  org_admin_name?: string | null;
}

export interface AcceptTermsResponse {
  user_id: string;
  org_id: string;
  terms_accepted: boolean;
  terms_accepted_at: string;
}

// Auth state for the application
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: ImsUserProfile | null;
  userOrgIds: string[];
  /** Map of orgId → human-readable organization name (from IMS /organizations/v6) */
  orgNames: Record<string, string>;
  error: string | null;
}

// Evaluation dataset types
export interface EvalDataset {
  dataset_id: string;
  name: string;
  description?: string;
  example_count: number;
  created_at: string;
  created_by?: string;
}

export interface EvalExampleItem {
  /** Stable LangSmith example id, present on rows loaded from the backend. */
  example_id?: string;
  question: string;
  answer: string;
  /** Document paths, URLs, or node IDs the golden answer should cite */
  expected_sources?: string[];
  /** ISO 8601 creation timestamp, server-derived from LangSmith. */
  created_at: string;
}

export interface EvalDatasetDetails extends EvalDataset {
  examples: EvalExampleItem[];
}

/** Body of the full-set upsert PUT for a golden set. */
export interface EvalDatasetUpdatePayload {
  name: string;
  description: string | null;
  examples: Array<{
    example_id?: string | null;
    question: string;
    answer: string;
    expected_sources: string[];
    /** Required by the backend's shared model; empty for rows with no server timestamp yet. */
    created_at: string;
  }>;
}

export interface EvalDatasetListResponse {
  datasets: EvalDataset[];
}

// Evaluation experiment types
export interface ExperimentStatus {
  experiment_id: string;
  dataset_id: string;
  dataset_name?: string;
  experiment_name?: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  total_examples: number;
  examples_completed?: number;
  error_message?: string;
  created_at: string;
  created_by?: string;
  started_at?: string;
  completed_at?: string;
  avg_scores?: Record<string, number>;
}

export interface ExperimentListResponse {
  experiments: ExperimentStatus[];
}

export interface ExperimentResultRow {
  question: string;
  expected_answer: string;
  actual_answer: string;
  expected_sources?: string[];
  actual_sources?: string[];
  answer_reasoning?: string;
  scores: Record<string, number>;
  score_reasons?: Record<string, string>;
  error?: string;
  execution_time?: number;
  total_tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  /** Subset of prompt_tokens served from the provider prompt cache (billed at a discount). Null for experiments run before cache accounting existed. */
  cached_prompt_tokens?: number | null;
  /** Subset of completion_tokens spent on model reasoning. Null for experiments run before reasoning accounting existed. */
  reasoning_tokens?: number | null;
}

export interface ContentOptimizationTopicSuggest {
  asset_guid: string;
  title: string;
  path: string;
  original_xml: string;
  optimized_xml: string;
  rationale: string;
}

export interface ContentOptimizationSuggestResponse {
  topics: ContentOptimizationTopicSuggest[];
}

export interface ContentOptimizationApplyTopic {
  asset_guid: string;
  optimized_xml: string;
  approve: boolean;
}

export interface ContentOptimizationApplyResponse {
  actual_answer: string;
  actual_sources: string[];
  scores: Record<string, number>;
  score_reasons: Record<string, string>;
  /** Subset of prompt tokens served from the provider prompt cache for the re-answer. Null when unavailable. */
  cached_prompt_tokens?: number | null;
  /** Subset of completion tokens spent on model reasoning for the re-answer. Null when unavailable. */
  reasoning_tokens?: number | null;
}

export interface ExperimentResultsSummary {
  total: number;
  successful: number;
  failed: number;
  avg_scores: Record<string, number>;
  total_tokens?: number;
  /** Sum of prompt tokens across rows (= uncached prompt + cached prompt). */
  total_prompt_tokens?: number;
  /** Sum of cached (discounted) prompt tokens across rows. */
  total_cached_prompt_tokens?: number;
  /** Sum of completion tokens across rows. */
  total_completion_tokens?: number;
  /** Sum of reasoning tokens across rows. */
  total_reasoning_tokens?: number;
}

export interface ExperimentResults {
  experiment_id: string;
  summary: ExperimentResultsSummary;
  rows: ExperimentResultRow[];
}

// Prompt configuration types
export interface SitePromptConfig {
  prompt_type: string;
  content: string;
  created_at?: string;
  updated_at?: string;
}

// Cache controls types
export interface CacheControlsSettings {
  /** null (legacy/never-saved controls) means enabled. */
  cache_enabled: boolean | null;
  cache_cutoff_date: string | null;
  similarity_threshold: number | null;
  last_indexed_at: number | null;
  last_prompt_changed_at: number | null;
  last_config_changed_at: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface CacheControlsRequest {
  cache_enabled: boolean;
  cache_cutoff_date: string | null;
  similarity_threshold: number;
}

// =============================================================================
// Command Center Types
// =============================================================================

export type SiteType = 'PROVISIONED' | 'SANDBOX';

export interface UsageInfo {
  used: number;
  total: number;
}

export interface CCOrganization {
  org_id: string;
  org_name: string;
  created_by: string;
  type: SiteType;
  domain: UsageInfo;
  request_usage: UsageInfo;
  document_indexed: UsageInfo;
  subscription_end: string | null;
  retrieval_algorithm: 'template_traversal' | 'guided_traversal';
  publishing_enabled: boolean;
  zip_size_limit_mb: number;
  pdf_size_limit_mb: number;
  max_dita_files_per_zip: number;
  has_org_admin: boolean;
}

export interface CCSite {
  site_id: string;
  org_id: string;
  site_name: string;
  description?: string | null;
  program_id?: string | null;
  env_id?: string | null;
  site_type: SiteType;
  created_by: string;
  created_at: string;
  updated_by?: string | null;
  updated_at?: string | null;
  /** Always empty from this endpoint -- the full per-utility configuration view
   * lives behind fetchDomainConfiguration (see CCDomainConfigurationEntry). */
  utility_configurations?: Record<string, UtilityConfigurationInfo>;
}

/** API 1 (requirement 2): an org's allowlisted configuration option for one utility. */
export interface CCDomainUtilityOption {
  configuration_id: string;
  configuration_name: string;
  is_default: boolean;
  is_deprecated: boolean;
  source: 'system_ootb' | 'org_custom';
  sunset_date?: string | null;
  model_name: string;
  provider_name: string;
}

export interface CCDomainUtilityOptionsPageResponse {
  items: CCDomainUtilityOption[];
  total: number;
  page: number;
  page_size: number;
}

/** API 2: can this org create a domain at all right now -- checked before the
 * create-domain form is even shown. Data-driven -- whichever utility names
 * are currently is_required, not a fixed 3-name list. */
export interface CCDomainUtilityRequirementsStatus {
  can_create_domain: boolean;
  required_utilities: string[];
  unsatisfied_utilities: string[];
  /** True iff a global system default exists for every required utility, so
   * the org can create a domain via "Use System defaults" even when its own
   * allowlist leaves required utilities unsatisfied. */
  system_defaults_available?: boolean;
}

/** requirement 5: a domain's current configuration for one utility, redacted. */
export interface CCDomainConfigurationEntry {
  configuration_id: string;
  configuration_name: string;
  source: 'system_ootb' | 'org_custom';
  is_deprecated: boolean;
  sunset_date?: string | null;
  is_default: boolean;
  credentials: Record<string, string>;
  behavioural_parameters: Record<string, unknown>;
}

export interface CCDomainConfigurationResponse {
  utility_configurations: Record<string, CCDomainConfigurationEntry>;
}

export interface CCOrganizationsResponse {
  organizations: CCOrganization[];
  total: number;
  total_all: number;
  total_approved: number;
  total_pending: number;
  page: number;
  page_size: number;
}

export interface CCOrganizationsSummary {
  total_orgs: number;
  total_domains: number;
  total_requests: number;
  total_documents_indexed: number;
}

export interface CCOrgSitesResponse {
  org_id: string;
  org_name: string;
  created_user_id: string;
  sites: CCSite[];
  total: number;
  page: number;
  page_size: number;
  total_sites: number;
  sandboxes: number;
  documents_indexed: number;
  total_documents_limit: number;
  remaining_request: number;
  total_request_limit: number;
  subscription_end: string | null;
}

export interface CCOrganizationUpdateRequest {
  type: SiteType;
  subscription_end: string;
  request_limit: number;
  document_index_limit: number;
  domain_limit: number;
  retrieval_algorithm: 'template_traversal' | 'guided_traversal';
  publishing_enabled: boolean;
  zip_size_limit_mb: number;
  pdf_size_limit_mb: number;
  max_dita_files_per_zip: number;
}

export interface CCDefaultConfig {
  subscription_end_interval: number;
  request_limit: number;
  document_index_limit: number;
  domain_limit: number;
  type: SiteType;
  retrieval_algorithm: 'template_traversal' | 'guided_traversal';
  publishing_enabled: boolean;
  zip_size_limit_mb: number;
  pdf_size_limit_mb: number;
  max_dita_files_per_zip: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CCDefaultConfigUpdateRequest {
  subscription_end_interval: number;
  request_limit: number;
  document_index_limit: number;
  domain_limit: number;
  type: SiteType;
  retrieval_algorithm: 'template_traversal' | 'guided_traversal';
  publishing_enabled: boolean;
  zip_size_limit_mb: number;
  pdf_size_limit_mb: number;
  max_dita_files_per_zip: number;
}

export interface CCOrgAdminEntry {
  user_id: string;
  display_name: string | null;
}

export interface CCOrgAdmins {
  org_id: string;
  admins: CCOrgAdminEntry[];
}

// =============================================================================
// Command Center — Models (registry + OOTB configurations)
// See docs/specs/command-center-model-config-spec.md for the backend contract
// this mirrors (mocked for now, see services/api/commandCenter.ts).
// =============================================================================

export type ModelRegistryType = 'llm' | 'embedding';

export interface CCProviderModelRegistryEntry {
  id: string;
  provider: ModelProvider;
  model: string;
  type: ModelRegistryType;
  isActive: boolean;
  isDeprecated: boolean;
  sunsetDate: string | null;
}

export interface CCOotbModelConfiguration {
  id: string;
  registryId: string;
  name: string;
  description?: string;
  provider: ModelProvider;
  model: string;
  type: ModelRegistryType;
  isValid: boolean;
  // A configuration's own deprecated flag — independent of the underlying
  // registry entry's isDeprecated (e.g. "Adobe Granite (Legacy)" can be
  // deprecated as a configuration even before/without the registry row
  // itself being marked deprecated).
  isDeprecated: boolean;
  updatedAt: string;
  updatedBy: string;
}

// Config on the wire is always grouped by FieldSchema.group — top-level keys
// are the group names ('credentials' | 'model' | 'behavioral'), each mapping
// to that group's { fieldName: value }. A request/response only includes the
// groups it actually has data for (see the API contract doc's grouping rule
// and app/models/command_center — config is Dict[str, Dict[str, Any]]).
export type CCGroupedConfig = Record<string, Record<string, unknown>>;

export interface CCOotbModelConfigurationRequest {
  registryId: string;
  name: string;
  description?: string;
  // Grouped by field class (credentials/model/behavioral) — NOT a flat
  // {fieldName: value} map. The dialog groups the form's flat values via
  // groupConfigByFieldClass() before calling, since that grouping needs the
  // provider's FieldSchema[] (each field's .group), which only the form has.
  config: CCGroupedConfig;
}

// Per-org allowlist — which OOTB configurations and which full-registry
// entries this org may use. Per design-frames/org-detail.html's finalized
// "Model Allowlist" tab, OOTB and Custom are two INDEPENDENT selections
// (both pools can have selections at once), not a mode-exclusive choice —
// this supersedes docs/specs/command-center-model-config-spec.md's earlier
// single-mode design, which the mockup iteration moved past.
//
// Each row arrives with its own `isAllowed` already resolved server-side (the
// GET .../allowlist/ootb and .../allowlist/custom endpoints), so the client
// never joins a separate "selected ids" response against a catalog. Saving is
// a delta of the rows whose isAllowed changed.
export interface CCOotbAllowlistEntry {
  configurationId: string;
  configurationName: string;
  provider: ModelProvider;
  model: string;
  type: ModelRegistryType;
  isAllowed: boolean;
  /** True when this config is a system default: always allowed for the org and
   * read-only here (it can't be removed from the OOTB allowlist). */
  isAllowlistLocked: boolean;
}

export interface CCCustomAllowlistEntry {
  registryId: string;
  provider: ModelProvider;
  model: string;
  type: ModelRegistryType;
  isAllowed: boolean;
}

/**
 * Usage of a single OOTB/Custom allowlist id being unselected, broken down
 * by org — a shared configuration/registry model can be referenced by
 * domains in orgs other than the one whose allowlist is being edited, so
 * this isn't scoped to just the current org. Backs the confirm-before-save
 * dialog shown when Save Changes would revoke an allowlist entry.
 */
export interface CCAllowlistUnselectUsage {
  id: string;
  usage: { orgId: string; orgName: string; domainCount: number }[];
}

/**
 * Usage of a single configuration id being unselected from the org Models
 * landing page's allowlist — scoped to just this org (a Custom
 * configuration only ever belongs to one org; an OOTB configuration's
 * cross-org usage isn't this screen's concern).
 */
export interface CCOrgModelUnselectUsage {
  configurationId: string;
  domainCount: number;
}

// ---------------------------------------------------------------------------
// Org-facing "Models" landing page (design-frames/12-landing-models.html) —
// a separate, org-admin-facing surface from the Command Center pages above.
// Here an org creates its OWN named model configuration ("Custom") with its
// own credentials, alongside the platform's OOTB configurations, and
// chooses which of either are enabled for its domains. This "Custom" is a
// distinct concept from CCCustomAllowlistEntry above (a
// Command-Center-admin-curated allowlist of raw registry entries, with no
// name or credentials of its own) — the two coexist as separate features.
// ---------------------------------------------------------------------------

/**
 * The three utility slots a domain's config maps onto (see CCSite's
 * llm_config/eval_llm_config/embedding_config) — each is served by at most
 * one org-wide configuration (OOTB or Custom) at a time.
 */
export type CCModelUtility = 'answering' | 'judge' | 'embedding';

/**
 * A single row in the org Models landing page — composed from either an
 * OOTB configuration or one of this org's own Custom configurations, plus
 * this org's own allow/override/utility state. Not a persisted entity on
 * its own — GET /platform/api/v1/models/{orgId}/configuration returns every
 * field already merged server-side, so the client never composes this from
 * separate calls. No `registryId` — the real response doesn't carry one
 * (provider/model/type already pin the underlying registry entry).
 */
export interface CCOrgModelEntry {
  id: string;
  name: string;
  description?: string;
  provider: ModelProvider;
  model: string;
  type: ModelRegistryType;
  source: 'ootb' | 'custom';
  isValid: boolean;
  isDeprecated: boolean;
  /** Sunset / deprecation date (ISO string) when known, else null. Shown as
   *  a tooltip on the Deprecated status in the org Models table. */
  sunsetDate?: string | null;
  /** Only meaningful when source === 'ootb': this org has a behavioural-param override for it. */
  isOverridden: boolean;
  /** Whether this org has enabled the configuration for domain creation. */
  allowed: boolean;
  /**
   * True when `allowed` holds only implicitly — the config is this org's
   * effective primary (a system default) with no explicit allowlist row to
   * revoke. The allowlist checkbox is read-only for these: opting out is done
   * by setting the org's own primary for that utility, not by un-checking.
   */
  isAllowlistLocked: boolean;
  /** Utility slots this org currently has assigned to this configuration. */
  utilities: CCModelUtility[];
}

/** POST /platform/api/v1/models/{orgId}/configuration — creates a new Custom configuration. */
export interface CCCreateCustomModelConfigurationRequest {
  registryId: string;
  name: string;
  description?: string;
  config: CCGroupedConfig;
}

/**
 * PUT /platform/api/v1/models/{orgId}/configuration/{id} — full edit for a
 * Custom configuration, or a behavioural-override-only edit for an OOTB one
 * (the backend rejects a non-behavioral group for an OOTB id). `name`/
 * `description` are required by the wire contract even for an OOTB edit,
 * where the backend ignores them — send the configuration's current values.
 */
export interface CCUpdateOrgModelConfigurationRequest {
  name: string;
  description?: string;
  config: CCGroupedConfig;
}

/**
 * The org's current primary-per-utility mapping, as read from GET
 * .../utility-assignments (a null slot means "no org primary; the effective
 * value falls back to the system default"). Writes go per-utility via
 * setOrgUtilityAssignment (PUT .../utility-assignments/{utility}); there is
 * no whole-object write and no remove.
 */
export type CCUtilityAssignments = Record<CCModelUtility, string | null>;

/**
 * GET .../utility-assignments row — one utility slot's current owner, with
 * its display name already resolved server-side (so a caller never needs to
 * cross-reference a possibly-incomplete local list to show it). Passing a
 * configurationId to the fetch narrows `utility` to that configuration's own
 * type's slots; omitting it returns all three.
 */
export interface CCUtilityAssignmentEntry {
  utility: CCModelUtility;
  configurationId: string | null;
  configurationName: string | null;
}

/**
 * GET /command-center/system-defaults row — the global default model
 * configuration for one utility (Adobe KG AI admin managed). `isMissingRequired`
 * flags a required utility with no default, which would block "Use System
 * defaults" domain creation.
 */
export interface CCSystemDefaultEntry {
  utility: CCModelUtility;
  configurationId: string | null;
  configurationName: string | null;
  provider: string | null;
  model: string | null;
  type: string | null;
  isMissingRequired: boolean;
}

/**
 * PUT /command-center/system-defaults body — partial update of utility slots.
 * A slot set to null clears its system default; omitted slots are unchanged.
 */
export type CCSystemDefaults = Partial<Record<CCModelUtility, string | null>>;

/**
 * GET .../registry/models — one of this org's custom-allowlisted registry
 * entries for a given type+provider (New Configuration wizard's Step B).
 * Carries deprecation state so the picker can warn before the user
 * configures a model that's already scheduled for sunset.
 */
export interface CCOrgRegistryModelEntry {
  registryId: string;
  model: string;
  isDeprecated: boolean;
  sunsetDate: string | null;
}

// ============================================================================
// Entity deduplication (human-in-the-loop review)
// ============================================================================
// TWO backends: the job lifecycle (analysis/merge trigger, poll, cancel) is
// owned by GKG; the candidate report + mark-distinct data path calls
// flameback-data directly (searchDocuments precedent). See
// services/api/entityDedup.ts.

/** Which phase a dedup job runs — analysis (candidate scan) or merge. */
export type EntityDedupJobPhase = 'analysis' | 'merge';

/** GKG dedup job lifecycle status (mirrors indexing's 6-state machine). */
export type EntityDedupJobStatus =
  | 'queued'
  | 'running'
  | 'interrupted'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** GKG `GET/POST .../entity-dedup/{analysis,merge,jobs}` response. */
export interface EntityDedupJobResponse {
  job_id: string;
  site_id: string;
  phase: EntityDedupJobPhase;
  /** Correlation id the data plane keys candidates on (= candidates `scan_id`). */
  run_id: string;
  status: EntityDedupJobStatus;
  entity_count: number;
  candidate_count: number;
  merged_count: number;
  failed_count: number;
  skipped_count: number;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  resume_count: number;
}

/** GKG `DELETE .../entity-dedup/jobs` response. */
export interface EntityDedupCancelResponse {
  job_id: string | null;
  /** 'cooperative', 'force', or 'none' (no active job). */
  cancel_type: string;
}

/** Data-plane analysis-run status — a 3-state view distinct from the 6-state
 * GKG job lifecycle: RUNNING (scan in progress), DONE (candidates ready to
 * review), FAILED (the scan errored, nothing to resume). */
export type EntityDedupScanStatus = 'RUNNING' | 'DONE' | 'FAILED';

/** flameback-data `GET .../entity-dedup/latest-scan` body — the most recent
 * ANALYSIS run for the site. The response is `null` when the site has never
 * been scanned. `run_id` == the candidates' `scan_id`, so the Review tab can
 * RESUME a completed scan's persisted candidates across sessions rather than
 * deriving the scan from the ephemeral job history (which a later merge job
 * displaces). */
export interface EntityDedupLatestScan {
  run_id: string;
  phase: 'analysis';
  status: EntityDedupScanStatus;
  entity_count: number;
  candidate_count: number;
  merged: number;
  failed: number;
  skipped: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/** Reviewer-prioritization band (sort hint only — never auto-merges). */
export type EntityDedupBand = 'HIGH' | 'REVIEW';

/** Lifecycle status of a candidate pair row. */
export type EntityDedupPairStatus = 'PENDING' | 'MERGED' | 'DISTINCT';

/** One entity in a candidate pair; identity is (site_id, normalized_name). */
export interface EntityRef {
  normalized_name: string;
  name: string;
  entity_type: string;
  mention_count: number;
  /** Human-readable summary of the entity. May be null/absent until the
   * flameback-data description field lands and a scan is re-run. */
  description?: string | null;
}

/** A single candidate duplicate pair (flameback-data report). */
export interface EntityDedupCandidate {
  pair_id: string;
  entity_a: EntityRef;
  entity_b: EntityRef;
  embed_score: number;
  band: EntityDedupBand;
  status: EntityDedupPairStatus;
}

/** flameback-data `GET .../entity-dedup/candidates` response. */
export interface EntityDedupCandidatesResponse {
  items: EntityDedupCandidate[];
  total: number;
}

/** Actioned status of an audited pair (subset of EntityDedupPairStatus). */
export type EntityDedupAuditStatus = 'MERGED' | 'DISTINCT';

/** One row of the unified entity-dedup audit log (flameback-data audit
 * endpoint). Reads the append-only records — `:MergeEvent` for MERGED,
 * `DISTINCT_FROM` edges for DISTINCT — site-wide across every scan. */
export interface EntityDedupAuditItem {
  pair_id: string;
  status: EntityDedupAuditStatus;
  /** For MERGED this is the SURVIVOR; for DISTINCT an arbitrary side-A. */
  entity_a: EntityRef;
  /** For MERGED the merged-away entity (its `name` is a normalized-name
   * fallback — the display name was physically deleted at merge); for DISTINCT
   * an arbitrary side-B. */
  entity_b: EntityRef;
  /** The survivor's normalized name — set for MERGED, null for DISTINCT. */
  survivor_normalized_name: string | null;
  /** Enriched from the candidate row; null when the candidate was pruned. */
  entity_type: string | null;
  embed_score: number | null;
  band: EntityDedupBand | null;
  /** Who decided — server-derived MergeEvent.actor / DISTINCT_FROM.by. */
  actor: string | null;
  /** ISO timestamp of the decision (MergeEvent.at / DISTINCT_FROM.at). */
  decided_at: string | null;
}

/** flameback-data `GET .../entity-dedup/audit` response. */
export interface EntityDedupAuditResponse {
  items: EntityDedupAuditItem[];
  total: number;
}
